import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { Box, Tooltip } from '@mui/material';
import { AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import Iconify from 'src/components/iconify';
import { GoogleMapWrapper } from 'src/pages/app/devices/map-google';
import { LatLngLiteral, PickTarget, SimSegment } from './types';

const SEGMENT_MARKER_GLYPHS: Record<string, { icon: string; title: string }> = {
  BREAK_30M: { icon: 'fluent-emoji-flat:cup-with-straw', title: '30m Break' },
  REST_10H: { icon: 'glyphs-poly:bed', title: '10H Break' },
  RESTART_34H: { icon: 'glyphs-poly:garage', title: '34H Break' },
};

function haversineMeters(a: google.maps.LatLng, b: google.maps.LatLng) {
  const earthRadius = 6371000;
  const dLat = ((b.lat() - a.lat()) * Math.PI) / 180;
  const dLng = ((b.lng() - a.lng()) * Math.PI) / 180;
  const lat1 = (a.lat() * Math.PI) / 180;
  const lat2 = (b.lat() * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.sqrt(h));
}

function pointAlongPath(path: google.maps.LatLng[], fraction: number): LatLngLiteral | null {
  if (path.length < 2) {
    return null;
  }
  let total = 0;
  const cumulative = [0];
  for (let i = 1; i < path.length; i += 1) {
    total += haversineMeters(path[i - 1], path[i]);
    cumulative.push(total);
  }
  if (total === 0) {
    return null;
  }
  const target = Math.min(Math.max(fraction, 0), 1) * total;
  for (let i = 1; i < path.length; i += 1) {
    if (cumulative[i] >= target) {
      const segmentLength = cumulative[i] - cumulative[i - 1];
      const t = segmentLength === 0 ? 0 : (target - cumulative[i - 1]) / segmentLength;
      return {
        lat: path[i - 1].lat() + (path[i].lat() - path[i - 1].lat()) * t,
        lng: path[i - 1].lng() + (path[i].lng() - path[i - 1].lng()) * t,
      };
    }
  }
  return { lat: path[path.length - 1].lat(), lng: path[path.length - 1].lng() };
}

function DirectionsLayer({
  directions,
  onRouteEdited,
}: {
  directions: google.maps.DirectionsResult | null;
  onRouteEdited: (directions: google.maps.DirectionsResult) => void;
}) {
  const map = useMap();
  const rendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  // the renderer both receives directions from us and produces them on drag;
  // tracking the last known result keeps the two flows from feeding each other
  const lastDirectionsRef = useRef<google.maps.DirectionsResult | null>(null);
  const onRouteEditedRef = useRef(onRouteEdited);
  onRouteEditedRef.current = onRouteEdited;

  useEffect(() => {
    if (!map) {
      return undefined;
    }
    if (!rendererRef.current) {
      rendererRef.current = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        draggable: true,
        polylineOptions: { strokeColor: '#288dff', strokeOpacity: 1, strokeWeight: 5 },
      });
      rendererRef.current.addListener('directions_changed', () => {
        const updated = rendererRef.current?.getDirections();
        if (updated && updated !== lastDirectionsRef.current) {
          lastDirectionsRef.current = updated;
          onRouteEditedRef.current(updated);
        }
      });
    }
    const renderer = rendererRef.current;
    if (directions) {
      if (lastDirectionsRef.current !== directions) {
        lastDirectionsRef.current = directions;
        renderer.setDirections(directions);
      }
      renderer.setMap(map);
    } else {
      renderer.setMap(null);
    }
    return () => {
      renderer.setMap(null);
    };
  }, [map, directions]);

  return null;
}

function MapClickListener({ picking, onMapClick }: { picking: boolean; onMapClick: (point: LatLngLiteral) => void }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !picking) {
      return undefined;
    }
    const listener = map.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        onMapClick({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      }
    });
    return () => {
      listener.remove();
    };
  }, [map, picking, onMapClick]);

  return null;
}

function GlyphMarker({ position, glyph, title, size = 30 }: { position: LatLngLiteral; glyph: ReactNode; title: string; size?: number }) {
  return (
    <AdvancedMarker position={position}>
      <Tooltip title={title} arrow placement="top">
        <Box
          component="div"
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: 'white',
            border: '2px solid #288dff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: size * 0.55,
            boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
          }}
        >
          {glyph}
        </Box>
      </Tooltip>
    </AdvancedMarker>
  );
}

export type RouteMapStop = {
  key: number;
  position: LatLngLiteral;
  glyph: ReactNode;
  title: string;
};

type RouteMapProps = {
  directions: google.maps.DirectionsResult | null;
  segments: SimSegment[];
  driverPosition: LatLngLiteral | null;
  stops: RouteMapStop[];
  picking: PickTarget;
  onMapClick: (point: LatLngLiteral) => void;
  onRouteEdited: (directions: google.maps.DirectionsResult) => void;
};

export default function RouteMap({
  directions,
  segments,
  driverPosition,
  stops,
  picking,
  onMapClick,
  onRouteEdited,
}: RouteMapProps) {
  const mapRef = useRef<google.maps.Map | null>(null);

  const stopMarkers = useMemo(() => {
    const path = directions?.routes[0]?.overview_path;
    if (!path) {
      return [];
    }
    return segments
      .filter((segment) => SEGMENT_MARKER_GLYPHS[segment.type])
      .map((segment, index) => {
        const config = SEGMENT_MARKER_GLYPHS[segment.type];
        return {
          key: `${segment.type}-${index}`,
          position: pointAlongPath(path, segment.startFraction),
          glyph: <Iconify icon={config.icon} width={17} />,
          title: config.title,
        };
      })
      .filter((marker) => marker.position !== null);
  }, [directions, segments]);

  return (
    <Box
      component="div"
      sx={{
        position: 'relative',
        width: '100%',
        height: { xs: 380, md: 520 },
        cursor: picking ? 'crosshair' : undefined,
        '& > div': { borderRadius: 1, overflow: 'hidden' },
      }}
    >
      <GoogleMapWrapper mapType="default" mapRef={mapRef}>
        <DirectionsLayer directions={directions} onRouteEdited={onRouteEdited} />
        <MapClickListener picking={picking !== null} onMapClick={onMapClick} />

        {driverPosition && (
          <GlyphMarker
            position={driverPosition}
            glyph={<Iconify icon="glyphs-poly:truck" width={20} />}
            title="Driver"
            size={34}
          />
        )}
        {stops.map((stop) => (
          <GlyphMarker key={stop.key} position={stop.position} glyph={stop.glyph} title={stop.title} />
        ))}

        {stopMarkers.map((marker) => (
          <GlyphMarker key={marker.key} position={marker.position!} glyph={marker.glyph} title={marker.title} />
        ))}
      </GoogleMapWrapper>
    </Box>
  );
}
